import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { ShoppingCart, Minus, Plus } from 'lucide-react';
import { api } from '../api/api';
import { Product } from '../types';
import { useStore } from '../store/useStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const addToCart = useStore(state => state.addToCart);

  useEffect(() => {
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(() => navigate(-1));

    return () => {
      WebApp.BackButton.hide();
    };
  }, [navigate]);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await api.getProduct(parseInt(id));
      setProduct(data);
    } catch (err) {
      setError('Не удалось загрузить товар');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }

    WebApp.showPopup({
      message: `Добавлено в корзину: ${quantity} шт.`,
      buttons: [
        { id: 'cart', type: 'default', text: 'Перейти в корзину' },
        { type: 'close' },
      ],
    }, (buttonId) => {
      if (buttonId === 'cart') {
        navigate('/cart');
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="p-4 m-4 bg-destructive/10 text-destructive rounded-lg">
        {error || 'Товар не найден'}
      </div>
    );
  }

  const totalPrice = parseFloat(product.price) * quantity;

  return (
    <div className="pb-6">
      {/* Product Image */}
      <div className="w-full aspect-square bg-secondary flex items-center justify-center">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-contain p-8"
          />
        ) : (
          <div className="text-muted-foreground">
            <ShoppingCart className="w-20 h-20" />
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="p-4 space-y-4">
        <div>
          <h1 className="text-2xl font-bold">{product.name}</h1>
          <p className="text-3xl font-bold text-primary mt-2">{product.price} ₽</p>
        </div>

        <div className="text-sm">
          {product.stock > 0 ? (
            <span className="text-green-600 font-medium">В наличии: {product.stock} шт.</span>
          ) : (
            <span className="text-destructive font-medium">Нет в наличии</span>
          )}
        </div>

        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2">Описание</h3>
            <p className="text-muted-foreground">{product.description}</p>
          </CardContent>
        </Card>

        {product.stock > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Количество:</span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-8 text-center font-semibold text-lg">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    disabled={quantity >= product.stock}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Button
          size="lg"
          className="w-full"
          onClick={handleAddToCart}
          disabled={product.stock === 0}
        >
          <ShoppingCart className="mr-2 h-5 w-5" />
          Добавить в корзину • {totalPrice} ₽
        </Button>
      </div>
    </div>
  );
}
